import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC, KeyboardEventHandler } from 'react';
import { Input, List, ListItem } from '@chakra-ui/react';
import { useCombobox } from 'downshift';
import type { UseComboboxState, UseComboboxStateChangeOptions } from 'downshift';
import { toAddressInputValue } from '../../../utils/address-utils';
import { isServer } from '../../../utils/utils';
import styles from './Combobox.module.scss';
import type { AutocompleteAddress } from '../../../types/radar';

type SplitAddress = {
    formattedAddress: string;
    startAddress: string;
    matchAddress?: string | null;
    endAddress?: string | null;
};

const splitAddress = (address: AutocompleteAddress, searchTerm: string): SplitAddress => {
    const { formattedAddress = '' } = address;
    const startIndex = formattedAddress.toLowerCase().indexOf(searchTerm);

    if (startIndex > -1) {
        const startAddress = formattedAddress.substring(0, startIndex);
        const matchAddress =
            formattedAddress.substring(startIndex, startIndex + searchTerm.length) ?? '';
        const endAddress = formattedAddress.substring(startIndex + searchTerm.length) ?? '';

        return {
            formattedAddress,
            startAddress,
            matchAddress,
            endAddress,
        };
    }

    return {
        formattedAddress,
        startAddress: formattedAddress,
    };
};

export type ComboboxProps = {
    items: AutocompleteAddress[];
    handleInputChange: (value: string) => void;
    handleSelect: (address: AutocompleteAddress) => void;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    inputProps: { id: string; name: string; autoComplete?: string; value?: string };
    variant?: string;
    useStreetAddressOnly?: boolean;
};

const getStreetAddress = (address: AutocompleteAddress | null): string => {
    if (!address) {
        return '';
    }
    const streetParts = [];
    if (address.number) {
        streetParts.push(address.number);
    }
    if (address.street) {
        streetParts.push(address.street);
    }
    return streetParts.join(' ').trim();
};

export const Combobox: FC<ComboboxProps> = ({
    items,
    handleInputChange,
    handleSelect,
    onKeyDown,
    inputProps,
    variant,
    useStreetAddressOnly = false,
}: ComboboxProps) => {
    const [isListBoxOpen, setIsListBoxOpen] = useState<boolean>(true);

    const stateReducer = useCallback(
        (
            state: UseComboboxState<AutocompleteAddress>,
            actionAndChanges: UseComboboxStateChangeOptions<AutocompleteAddress>,
        ): Partial<UseComboboxState<AutocompleteAddress>> => {
            const { type, changes } = actionAndChanges;

            const isSoftKeyboardOpen =
                !isServer &&
                window.visualViewport &&
                window.innerHeight > window.visualViewport?.height;

            switch (type) {
                case useCombobox.stateChangeTypes.InputBlur:
                    return {
                        ...changes,
                        ...(isSoftKeyboardOpen ? { isOpen: true } : {}),
                    };
                default:
                    return { ...changes };
            }
        },
        [],
    );

    const { getInputProps, getMenuProps, getItemProps, isOpen, highlightedIndex } = useCombobox({
        items,
        inputValue: inputProps.value,
        onInputValueChange: (newValue) => {
            const { inputValue } = newValue;
            handleInputChange(inputValue || '');
        },
        onSelectedItemChange: ({ selectedItem }) => {
            handleSelect(selectedItem as AutocompleteAddress);
        },
        itemToString: useStreetAddressOnly ? getStreetAddress : toAddressInputValue,
        stateReducer,
    });

    useEffect(() => {
        setIsListBoxOpen((inputProps.value?.length ?? 0) >= 3 && items.length > 0 && isOpen);
    }, [inputProps.value, items, isOpen]);

    const inputPropsWithoutValue = useMemo<object>(() => {
        const { value, ...result } = inputProps;
        return result;
    }, [inputProps]);

    return (
        <>
            <Input
                {...getInputProps()}
                {...inputPropsWithoutValue}
                css={{ borderRadius: '0.25rem 0.25rem 0 0' }}
                autoComplete={inputProps.autoComplete}
                data-testid={inputProps.id}
                variant={variant}
                onKeyDown={onKeyDown}
            />
            {isListBoxOpen && (
                <List
                    className={`w-full border border-t-0 border-solid ${styles.comboboxList}`}
                    variant={variant}
                    data-testid={`${inputProps.id}-list`}
                    {...getMenuProps({}, { suppressRefError: true })}
                >
                    {items.map((item, index) => {
                        const address = splitAddress(item, inputProps.value ?? '');

                        return (
                            <ListItem
                                padding={variant ? undefined : '0.5rem'}
                                backgroundColor={highlightedIndex === index ? '#F6F5F6' : ''}
                                // eslint-disable-next-line react/no-array-index-key
                                key={address.formattedAddress.replace(/\s/g, '') + index}
                                data-testid={`${inputProps.id}-list-item-${index}`}
                                {...getItemProps({ item, index })}
                            >
                                <span>
                                    {address.startAddress}
                                    {address.matchAddress ? (
                                        <>
                                            <strong>{address.matchAddress}</strong>
                                            {address.endAddress}
                                        </>
                                    ) : (
                                        ''
                                    )}
                                </span>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </>
    );
};

Combobox.defaultProps = {
    variant: undefined,
    onKeyDown: undefined,
    useStreetAddressOnly: false,
};
